/**
 * TanStack Query mutation hooks for the admin catalog-transfer surface —
 * PIM Wave P5 Task 8. All three operations are admin-triggered, one-shot
 * actions (never cached queries) — `useMutation` mirrors `import/hooks.ts`'s
 * `useUploadImport`/`useCommitImport` idiom (an on-demand action with a
 * `isPending` loading flag) rather than `useQuery`, even for the read-only
 * `export`/`dry-run` calls.
 *
 * A successful `import/apply` creates a draft changeset — invalidate the
 * changesets list so `ChangesetsPage`/`PendingChangesBanner` pick it up
 * immediately (mirrors `changesets/hooks.ts`'s own list-prefix invalidate).
 */
import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";

import {
  applyTransferImport,
  dryRunTransferImport,
  exportTransferBundle,
  type ImportApplyResponse,
  type TransferBundle,
  type TransferDiff,
} from "@/features/admin/transfer/api";

export function useExportTransferBundle(): UseMutationResult<TransferBundle, Error, void> {
  return useMutation<TransferBundle, Error, void>({
    mutationFn: () => exportTransferBundle(),
  });
}

export function useDryRunTransferImport(): UseMutationResult<TransferDiff, Error, unknown> {
  return useMutation<TransferDiff, Error, unknown>({
    mutationFn: (bundle) => dryRunTransferImport(bundle),
  });
}

export function useApplyTransferImport(): UseMutationResult<
  ImportApplyResponse,
  Error,
  { bundle: unknown; title?: string }
> {
  const qc = useQueryClient();
  return useMutation<ImportApplyResponse, Error, { bundle: unknown; title?: string }>({
    mutationFn: ({ bundle, title }) => applyTransferImport(bundle, title),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "changesets", "list"] });
    },
  });
}
